import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Grid3x3, PieChart, Blocks, Divide, Hash, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { BottomNav } from "@/components/student/BottomNav";

const TOOLS = [
  { id: "array", name: "Multiplication Array", icon: Grid3x3, color: "#6366F1" },
  { id: "fractions", name: "Fractions", icon: PieChart, color: "#EC4899" },
  { id: "placevalue", name: "Place-Value Blocks", icon: Blocks, color: "#F59E0B" },
  { id: "division", name: "Division Grouping", icon: Divide, color: "#10B981" },
  { id: "decimals", name: "Decimals Grid", icon: Hash, color: "#06B6D4" },
  { id: "algebra", name: "Algebra Balance", icon: Scale, color: "#8B5CF6" },
];

export default function VisualizationPage() {
  const navigate = useNavigate();
  const [tool, setTool] = useState("array");

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-white border-b px-4 py-3">
        <button onClick={() => navigate("/student")} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-semibold" data-testid="viz-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="visualize-page">
        <h1 className="text-3xl font-extrabold text-slate-800 font-['Fredoka'] mb-1">Maths Visualisation</h1>
        <p className="text-slate-500 mb-5 font-['Lexend']">See the maths — play with each model, no pressure, no score.</p>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`viz-tool-${t.id}`}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border-2 transition-all ${tool === t.id ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}
              style={tool === t.id ? { background: t.color } : {}}>
              <t.icon className="w-4 h-4" /> {t.name}
            </button>
          ))}
        </div>

        <div className="rounded-3xl bg-white border-2 border-slate-200 p-5">
          {tool === "array" && <ArrayTool />}
          {tool === "fractions" && <FractionTool />}
          {tool === "placevalue" && <PlaceValueTool />}
          {tool === "division" && <DivisionTool />}
          {tool === "decimals" && <DecimalTool />}
          {tool === "algebra" && <AlgebraTool />}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

function ArrayTool() {
  const [a, setA] = useState(3);
  const [b, setB] = useState(4);
  return (
    <div data-testid="viz-array">
      <ControlRow label={`Rows: ${a}`}><Slider min={1} max={10} value={[a]} onValueChange={(v) => setA(v[0])} /></ControlRow>
      <ControlRow label={`Columns: ${b}`}><Slider min={1} max={10} value={[b]} onValueChange={(v) => setB(v[0])} /></ControlRow>
      <p className="text-center text-2xl font-extrabold text-indigo-600 font-['Fredoka'] my-3">{a} × {b} = {a * b}</p>
      <div className="flex justify-center">
        <div className="inline-grid gap-1.5" style={{ gridTemplateColumns: `repeat(${b}, minmax(0,1fr))` }}>
          {Array.from({ length: a * b }).map((_, i) => (
            <div key={i} className="w-6 h-6 rounded-full bg-indigo-500 shadow-sm transition-transform" style={{ transitionDelay: `${i * 10}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FractionTool() {
  const [num, setNum] = useState(3);
  const [den, setDen] = useState(8);
  const n = Math.min(num, den);
  const pct = Math.round((n / den) * 100);
  const deg = (n / den) * 360;
  return (
    <div data-testid="viz-fractions">
      <ControlRow label={`Numerator: ${n}`}><Slider min={0} max={den} value={[n]} onValueChange={(v) => setNum(v[0])} /></ControlRow>
      <ControlRow label={`Denominator: ${den}`}><Slider min={1} max={12} value={[den]} onValueChange={(v) => { setDen(v[0]); setNum((x) => Math.min(x, v[0])); }} /></ControlRow>
      <p className="text-center text-2xl font-extrabold text-pink-600 font-['Fredoka'] my-3">{n}/{den} = {(n / den).toFixed(2)} = {pct}%</p>
      <div className="flex items-center justify-center gap-8 flex-wrap">
        <div className="w-40 h-40 rounded-full shadow-inner" style={{ background: `conic-gradient(#EC4899 ${deg}deg, #FCE7F3 ${deg}deg 360deg)` }} data-testid="fraction-pie" />
        <div className="flex w-56 h-12 rounded-xl overflow-hidden border-2 border-pink-200">
          {Array.from({ length: den }).map((_, i) => (
            <div key={i} className={`flex-1 border-r border-white ${i < n ? "bg-pink-500" : "bg-pink-100"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PlaceValueTool() {
  const [num, setNum] = useState(1234);
  const digits = String(Math.max(0, Math.min(9999, num))).padStart(4, "0").split("").map(Number);
  const places = [
    { label: "Thousands", count: digits[0], color: "#F59E0B" },
    { label: "Hundreds", count: digits[1], color: "#10B981" },
    { label: "Tens", count: digits[2], color: "#6366F1" },
    { label: "Units", count: digits[3], color: "#EC4899" },
  ];
  return (
    <div data-testid="viz-placevalue">
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className="text-sm text-slate-500">Number:</span>
        <Input type="number" value={num} onChange={(e) => setNum(parseInt(e.target.value) || 0)} className="w-28 h-10 text-center font-bold text-lg" data-testid="placevalue-input" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {places.map((p) => (
          <div key={p.label} className="rounded-xl border border-slate-200 p-2 text-center">
            <div className="flex flex-wrap gap-1 justify-center items-end min-h-[80px]">
              {Array.from({ length: p.count }).map((_, i) => (
                <div key={i} className="w-3.5 h-3.5 rounded-sm" style={{ background: p.color }} />
              ))}
            </div>
            <p className="text-lg font-extrabold font-['Fredoka']" style={{ color: p.color }}>{p.count}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">{p.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DivisionTool() {
  const [a, setA] = useState(13);
  const [b, setB] = useState(4);
  const q = Math.floor(a / b);
  const rem = a % b;
  const groups = Array.from({ length: b }, (_, gi) => Math.floor(a / b) + (gi < rem ? 1 : 0));
  return (
    <div data-testid="viz-division">
      <ControlRow label={`Total: ${a}`}><Slider min={1} max={30} value={[a]} onValueChange={(v) => setA(v[0])} /></ControlRow>
      <ControlRow label={`Groups: ${b}`}><Slider min={1} max={8} value={[b]} onValueChange={(v) => setB(v[0])} /></ControlRow>
      <p className="text-center text-2xl font-extrabold text-emerald-600 font-['Fredoka'] my-3">{a} ÷ {b} = {q}{rem ? ` remainder ${rem}` : ""}</p>
      <div className="flex flex-wrap gap-3 justify-center">
        {groups.map((cnt, gi) => (
          <div key={gi} className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-2 flex flex-wrap gap-1 justify-center w-20 min-h-[60px] content-start">
            {Array.from({ length: cnt }).map((_, i) => (
              <div key={i} className="w-4 h-4 rounded-full bg-emerald-500" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function DecimalTool() {
  const [v, setV] = useState(0.42);
  const shaded = Math.round(v * 100);
  return (
    <div data-testid="viz-decimals">
      <ControlRow label={`Value: ${v.toFixed(2)}`}><Slider min={0} max={1} step={0.01} value={[v]} onValueChange={(x) => setV(x[0])} /></ControlRow>
      <p className="text-center text-2xl font-extrabold text-cyan-600 font-['Fredoka'] my-3">{v.toFixed(2)} = {shaded}/100 = {shaded}%</p>
      <div className="flex justify-center">
        <div className="grid grid-cols-10 gap-0.5 p-1 bg-cyan-100 rounded-lg" data-testid="decimal-grid">
          {Array.from({ length: 100 }).map((_, i) => (
            <div key={i} className={`w-5 h-5 rounded-[3px] ${i < shaded ? "bg-cyan-500" : "bg-white"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AlgebraTool() {
  const [a, setA] = useState(3);
  const [b, setB] = useState(8);
  const solution = b - a;
  const [guess, setGuess] = useState(0);
  const leftWeight = guess + a;
  const balanced = leftWeight === b;
  const tilt = Math.max(-12, Math.min(12, (leftWeight - b) * 2));
  return (
    <div data-testid="viz-algebra">
      <ControlRow label={`Add (a): ${a}`}><Slider min={0} max={9} value={[a]} onValueChange={(v) => { setA(v[0]); setGuess(0); }} /></ControlRow>
      <ControlRow label={`Equals (b): ${b}`}><Slider min={1} max={15} value={[b]} onValueChange={(v) => { setB(v[0]); setGuess(0); }} /></ControlRow>
      <p className="text-center text-2xl font-extrabold text-violet-600 font-['Fredoka'] my-2">x + {a} = {b}</p>
      <ControlRow label={`Your guess for x: ${guess}`}><Slider min={0} max={15} value={[guess]} onValueChange={(v) => setGuess(v[0])} data-testid="algebra-guess" /></ControlRow>
      <div className="relative h-32 flex items-end justify-center mt-2">
        <div className="absolute bottom-0 w-2 h-24 bg-slate-400 rounded" />
        <div className="absolute bottom-24 w-72 h-2 bg-slate-600 rounded origin-center transition-transform" style={{ transform: `rotate(${tilt}deg)` }}>
          <div className="absolute -left-2 -top-10 w-20 h-10 rounded-lg bg-violet-200 border-2 border-violet-400 flex items-center justify-center text-xs font-bold text-violet-700">x+{a}={leftWeight}</div>
          <div className="absolute -right-2 -top-10 w-20 h-10 rounded-lg bg-indigo-200 border-2 border-indigo-400 flex items-center justify-center text-xs font-bold text-indigo-700">{b}</div>
        </div>
      </div>
      <p className="text-center mt-2 font-bold" data-testid="algebra-status">
        {balanced ? <span className="text-green-600">Balanced! x = {solution} ✓</span> : <span className="text-slate-400">Tilt the scale until it balances…</span>}
      </p>
    </div>
  );
}

function ControlRow({ label, children }) {
  return (
    <div className="mb-3">
      <p className="text-xs font-semibold text-slate-500 mb-1.5">{label}</p>
      {children}
    </div>
  );
}
